import { FC, useMemo, useState, useEffect, useRef } from 'react';
import {
  RadixTable,
  TColumn,
  Tab,
  Button,
  Input,
  Label,
  DatePicker,
  InputSelect,
} from '@features/ui';
import { UserProfile } from '@src/types/users.ts';
import { Invoice } from '@src/types/invoices';
import { formatDateTimeShort } from '@lib/utils.ts';
import { getUserInvoices, getUserPayments } from '@src/services/users';
import { toast } from 'react-toastify';
import { useProductOptions } from '@hooks/useProductOptions.tsx';
import { useNavigate } from 'react-router-dom';

interface PaymentsAccessProps {
  user: UserProfile | null;
  targetInvoiceId?: number | null;
}

interface UserInvoicesProps {
  userId: number;
  targetInvoiceId?: number | null;
}

// Status mapping with colors
const getInvoiceStatusDisplay = (status: string) => {
  const statusMap: Record<string, { text: string; colorClass: string }> = {
    pending: { text: 'Pending', colorClass: 'text-yellow-500' },
    paid: { text: 'Paid', colorClass: 'text-green-500' },
    recurring_active: { text: 'Recurring Active', colorClass: 'text-blue-500' },
    recurring_cancelled: {
      text: 'Recurring Cancelled',
      colorClass: 'text-red-500',
    },
    recurring_failed: {
      text: 'Recurring Failed',
      colorClass: 'text-red-500',
    },
    recurring_finished: {
      text: 'Recurring Finished',
      colorClass: 'text-gray-500',
    },
    approved: { text: 'Approved', colorClass: 'text-green-500' },
  };

  return statusMap[status] || { text: status, colorClass: 'text-gray-400' };
};
const formatPeriod = (period: string | null | undefined): string => {
  if (!period) return '';

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

  if (dateRegex.test(period)) {
    return '';
  }

  const periodMap: Record<string, string> = {
    '1d': '1 day',
    '1m': '1 month',
    '1y': '1 year',
    '2d': '2 days',
    '3d': '3 days',
    '7d': '7 days',
    '2m': '2 months',
    '3m': '3 months',
    '6m': '6 months',
    '2y': '2 years',
  };

  return periodMap[period] || period;
};

const UserInvoices: FC<UserInvoicesProps> = ({ userId, targetInvoiceId }) => {
  const navigate = useNavigate();
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const lastAutoExpandedInvoiceId = useRef<number | null>(null);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!userId) return;

      setLoading(true);
      try {
        const response = await getUserInvoices(userId);
        const nextInvoices = response.data.invoices || [];

        setInvoices(nextInvoices);

        if (targetInvoiceId) {
          const matchedInvoice = nextInvoices.find(
            (invoice) => Number(invoice.id) === targetInvoiceId
          );

          if (matchedInvoice) {
            lastAutoExpandedInvoiceId.current = targetInvoiceId;
            setExpandedInvoice(String(matchedInvoice.id));

            requestAnimationFrame(() => {
              const targetElement = document.getElementById(
                `invoice-${matchedInvoice.id}`
              );

              targetElement?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            });
          }
        }
      } catch (error) {
        console.error('Error fetching invoices:', error);
        toast.error('Failed to load invoices');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [userId, targetInvoiceId]);

  const toggleInvoice = (id: number) => {
    setExpandedInvoice((prev) => (Number(prev) === id ? null : id.toString()));
  };

  useEffect(() => {
    if (!targetInvoiceId || loading || invoices.length === 0) return;
    if (lastAutoExpandedInvoiceId.current === targetInvoiceId) return;

    const matchedInvoice = invoices.find(
      (invoice) => Number(invoice.id) === targetInvoiceId
    );

    if (!matchedInvoice) return;

    lastAutoExpandedInvoiceId.current = targetInvoiceId;
    setExpandedInvoice(matchedInvoice.id.toString());

    requestAnimationFrame(() => {
      const targetElement = document.getElementById(`invoice-${matchedInvoice.id}`);

      targetElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [targetInvoiceId, loading, invoices]);

  if (loading) {
    return <div className="text-center py-8">Loading invoices...</div>;
  }

  if (invoices.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No invoices found for this user.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold mb-4">User Invoices</h2>

      {invoices.map((invoice) => {
        const isExpanded = expandedInvoice === invoice.id.toString();
        const statusDisplay = getInvoiceStatusDisplay(invoice.status);

        return (
          <div
            key={invoice.id}
            className="border border-gray-700 rounded"
            id={`invoice-${invoice.id}`}
          >
            {/* Invoice Header */}
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-800/30"
              onClick={() => toggleInvoice(invoice.id)}
            >
              <div className="flex items-center gap-4 flex-1">
                <button className="text-gray-400">
                  {isExpanded ? '▼' : '▶'}
                </button>
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="font-mono">
                    #{invoice.id}/{invoice.legacy_invoice_public_id}
                  </span>
                  <span className="text-gray-400">
                    {invoice.payment_system},
                  </span>
                  <span className={statusDisplay.colorClass}>
                    {statusDisplay.text}
                  </span>
                  {invoice.first_total && invoice.first_period && (
                    <span>
                      Billing Terms:{' '}
                      <strong>
                        ${invoice.first_total}
                        {formatPeriod(invoice.first_period) &&
                          ` for the ${formatPeriod(invoice.first_period)}`}
                        , then ${invoice.second_total}
                        {formatPeriod(invoice.second_period) &&
                          ` for the ${formatPeriod(invoice.second_period)}`}
                      </strong>
                    </span>
                  )}
                  <span>
                    created at{' '}
                    <strong>{formatDateTimeShort(invoice.created_at)}</strong>,
                  </span>
                  <span>
                    first paid at{' '}
                    <strong>
                      {invoice.paid_at
                        ? formatDateTimeShort(invoice.paid_at)
                        : ''}
                    </strong>
                  </span>
                  {invoice.coupon_code && (
                    <button
                      className="text-blue-400 hover:underline"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!invoice.coupon_code) return;
                        navigate(
                          `/admin/coupons?code=${encodeURIComponent(invoice.coupon_code)}`
                        );
                      }}
                    >
                      Coupon: {invoice.coupon_code}
                    </button>
                  )}
                  {invoice.product?.title && (
                    <span className="text-blue-400">
                      origin: {invoice.product?.title}
                    </span>
                  )}
                </div>
              </div>
              {/*<div className="flex items-center gap-3">*/}
              {/*  <button className="text-blue-400 hover:underline text-sm">*/}
              {/*    Transactions Log*/}
              {/*  </button>*/}
              {/*  <span className="text-gray-500">|</span>*/}
              {/*  <button className="text-blue-400 hover:underline text-sm">*/}
              {/*    Data*/}
              {/*  </button>*/}
              {/*</div>*/}
            </div>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="border-t border-gray-700 p-4 bg-gray-900/20">
                <div className="grid grid-cols-2 gap-8">
                  {/* Invoice Items / Products */}
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Products</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="text-left pb-2 font-medium px-2">
                              #
                            </th>
                            <th className="text-left pb-2 font-medium px-2">
                              TITLE
                            </th>
                            <th className="text-left pb-2 font-medium px-2">
                              {/* Empty column for labels */}
                            </th>
                            <th className="text-right pb-2 font-medium px-2">
                              FIRST PRICE
                            </th>
                            <th className="text-right pb-2 font-medium px-2">
                              SECOND PRICE
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.invoice_items.map((item) => (
                            <>
                              {/* Main row */}
                              <tr
                                key={item.id}
                                className="border-b border-gray-800"
                              >
                                <td className="py-2 px-2">
                                  {item.item_type === 'product'
                                    ? item.product_id
                                    : ''}
                                </td>
                                <td className="py-2 px-2">
                                  <div>{item.item_title}</div>
                                  {item.description && (
                                    <div className="text-xs text-gray-400">
                                      {item.description}
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 px-2 text-left" />
                                <td className="py-2 px-2 text-right">
                                  ${item.unit_price}
                                </td>
                                <td className="py-2 px-2 text-right">
                                  {item.second_unit_price
                                    ? `$${item.second_unit_price}`
                                    : '-'}
                                </td>
                              </tr>
                              {/* Discount row */}
                              {(parseFloat(item.discount_amount) > 0 ||
                                (item.second_discount_amount &&
                                  parseFloat(item.second_discount_amount) >
                                    0)) && (
                                <tr className="border-b border-gray-800">
                                  <td className="py-1 px-2" />
                                  <td className="py-1 px-2" />
                                  <td className="py-1 px-2 text-left text-xs text-gray-400">
                                    DISCOUNT
                                  </td>
                                  <td className="py-1 px-2 text-right text-green-500 text-xs">
                                    {item.discount_amount
                                      ? `$${item.discount_amount}`
                                      : '$0.00'}
                                  </td>
                                  <td className="py-1 px-2 text-right text-green-500 text-xs">
                                    {item.second_discount_amount
                                      ? `$${item.second_discount_amount}`
                                      : '$0.00'}
                                  </td>
                                </tr>
                              )}
                              {(parseFloat(item.tax_amount) > 0 ||
                                (item.second_tax_amount &&
                                  parseFloat(item.second_tax_amount) > 0)) && (
                                <tr className="border-b border-gray-800">
                                  <td className="py-1 px-2" />
                                  <td className="py-1 px-2" />
                                  <td className="py-1 px-2 text-left text-xs text-gray-400">
                                    TAX
                                  </td>
                                  <td className="py-1 px-2 text-right text-xs">
                                    {item.tax_amount
                                      ? `$${item.tax_amount}`
                                      : '$0.00'}
                                  </td>
                                  <td className="py-1 px-2 text-right text-xs">
                                    {item.second_tax_amount
                                      ? `$${item.second_tax_amount}`
                                      : '$0.00'}
                                  </td>
                                </tr>
                              )}
                              {/* Total row */}
                              <tr>
                                <td className="py-1 px-2" />
                                <td className="py-1 px-2" />
                                <td className="py-1 px-2 text-left text-xs font-semibold">
                                  TOTAL
                                </td>
                                <td className="py-1 px-2 text-right font-semibold text-xs">
                                  ${item.total_amount ?? '0.00'}
                                </td>
                                <td className="py-1 px-2 text-right font-semibold text-xs">
                                  {item.second_total_amount
                                    ? `$${item.second_total_amount}`
                                    : '$0.00'}
                                </td>
                              </tr>
                            </>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Additional Info */}
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold mb-3">
                        Tax Information
                      </h3>
                      <div className="space-y-2 text-sm">
                        {invoice.coupon_code && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Coupon Code</span>
                            <span className="text-blue-400">
                              {invoice.coupon_code}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-400">Currency</span>
                          <span>{invoice.currency}</span>
                        </div>
                        {invoice.refunded_amount &&
                          parseFloat(invoice.refunded_amount) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-400">
                                Refunded Amount
                              </span>
                              <span className="text-red-500">
                                ${invoice.refunded_amount}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Payments */}
                  <div>
                    <h3 className="text-sm font-semibold mb-3">Payments</h3>
                    {invoice.invoice_payments.length > 0 ? (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="text-left pb-2 font-medium">#</th>
                            <th className="text-left pb-2 font-medium">
                              DATE/TIME
                            </th>
                            <th className="text-left pb-2 font-medium">
                              PAYSYSTEM
                            </th>
                            <th className="text-left pb-2 font-medium">
                              INVOICE
                            </th>
                            <th className="text-right pb-2 font-medium">
                              AMOUNT
                            </th>
                            <th className="text-right pb-2 font-medium" />
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.invoice_payments.map((payment) => (
                            <tr
                              key={payment.id}
                              className="border-b border-gray-800"
                            >
                              <td className="py-2">{payment.id}</td>
                              <td className="py-2">
                                {formatDateTimeShort(payment.paid_at)}
                              </td>
                              <td className="py-2">{payment.payment_system}</td>
                              <td className="py-2 text-xs">
                                {payment.display_invoice_id}
                              </td>
                              <td className="py-2 text-right">
                                ${payment.amount}
                              </td>
                              {/*<td className="py-2 text-right">*/}
                              {/*  <button className="text-blue-400 hover:underline text-xs">*/}
                              {/*    Send Receipt*/}
                              {/*  </button>*/}
                              {/*</td>*/}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-gray-400 text-sm">
                        No payments recorded yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Payments
interface PaymentsTableProps {
  userId: number;
}

const PaymentsTable: FC<PaymentsTableProps> = ({ userId }) => {
  type Row = {
    id: number;
    created_at: string;
    invoice: string;
    receipt: string;
    system: string;
    amount: string;
    tax: string;
    refunded: string;
    items: string;
  };
  const { productOptions } = useProductOptions();

  const [data, setData] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search_word: '',
    product_id: null as number | null,
    payment_system: null as string | null,
    payment_start_date: null as string | null,
    payment_end_date: null as string | null,
    user_added_start_date: null as string | null,
    user_added_end_date: null as string | null,
    search_field: '' as string | null,
  });

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await getUserPayments(userId, {
        product_id: filters.product_id,
        payment_system: filters.payment_system,
        payment_start_date: filters.payment_start_date,
        payment_end_date: filters.payment_end_date,
        user_added_start_date: filters.user_added_start_date,
        user_added_end_date: filters.user_added_end_date,
        search_word: filters.search_word || null,
        search_field: filters.search_field || null,
      });

      const mappedData: Row[] = (response.data.payments || []).map(
        (payment) => ({
          id: payment.id,
          created_at: payment.paid_at,
          invoice: payment.display_invoice_id,
          receipt: payment.external_receipt_id || '-',
          system: payment.payment_system,
          amount: `$${payment.amount}`,
          tax: `$${payment.tax_amount}`,
          refunded: payment.refunded_amount
            ? `$${payment.refunded_amount}`
            : '$0.00',
          items: payment.product_title || '-',
        })
      );

      setData(mappedData);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchPayments();
  };

  const columns: TColumn<Row>[] = [
    {
      title: 'DATE/TIME',
      render: (r) => (
        <span>{r?.created_at ? formatDateTimeShort(r.created_at) : '—'}</span>
      ),
    },
    {
      title: 'INVOICE',
      render: (r) => (
        <span className="text-blue-400 underline cursor-pointer">
          {r?.invoice ?? '—'}
        </span>
      ),
    },
    { title: 'RECEIPT', render: (r) => <span>{r?.receipt ?? '—'}</span> },
    {
      title: 'PAYMENT SYSTEM',
      render: (r) => <span className="capitalize">{r?.system ?? '—'}</span>,
    },
    { title: 'AMOUNT', render: (r) => <span>{r?.amount ?? '—'}</span> },
    { title: 'TAX', render: (r) => <span>{r?.tax ?? '—'}</span> },
    { title: 'REFUNDED', render: (r) => <span>{r?.refunded ?? '—'}</span> },
    { title: 'ITEMS', render: (r) => <span>{r?.items ?? '—'}</span> },
  ];

  console.log('productOptions', productOptions);

  return (
    <div>
      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input
          className="w-[220px]"
          placeholder="Search…"
          value={filters.search_word}
          variant="secondary"
          onChange={(e) => handleFilterChange('search_word', e.target.value)}
        />
        <InputSelect
          className="w-[180px]"
          options={productOptions}
          placeholder="-Filter by Product-"
          value={filters.product_id as unknown as string}
          onChange={(value) =>
            handleFilterChange('product_id', value ? Number(value) : null)
          }
        />
        <InputSelect
          className="w-[180px]"
          options={[
            { value: 'paypal', label: 'PayPal' },
            { value: 'stripe', label: 'Stripe' },
            { value: 'paypal_express', label: 'PayPal Express' },
            { value: 'network_merchants_inc', label: 'Network Merchants Inc' },
            { value: 'apple_pay', label: 'ApplePay' },
            { value: 'free_signup', label: 'Free Signup' },
          ]}
          placeholder="-Filter by Paysystem-"
          value={filters.payment_system ?? ''}
          onChange={(value) =>
            handleFilterChange('payment_system', value || null)
          }
        />
        <Label className="text-xs text-gray-500">Payment Date:</Label>
        <DatePicker
          className="w-[150px] h-9 rounded-[3px] text-sm"
          value={filters.payment_start_date || undefined}
          onChange={(date) =>
            handleFilterChange(
              'payment_start_date',
              date ? date.toISOString().split('T')[0] : null
            )
          }
        />
        <DatePicker
          className="w-[150px] h-9.5 rounded-[3px] text-sm"
          value={filters.payment_end_date || undefined}
          onChange={(date) =>
            handleFilterChange(
              'payment_end_date',
              date ? date.toISOString().split('T')[0] : null
            )
          }
        />
        <InputSelect
          className="w-[160px]"
          options={[
            { value: 'invoice_id', label: 'Invoice ID' },
            { value: 'payment_receipt', label: 'Payment Receipt' },
            { value: 'username', label: 'Username' },
            { value: 'name', label: 'Name' },
            { value: 'country', label: 'Country' },
            { value: 'affiliate_username', label: 'Affiliate Username' },
            { value: 'coupon_code', label: 'Coupon Code' },
            { value: 'item_title', label: 'Item Title' },
            { value: 'payment_ids', label: 'Payment IDs' },
          ]}
          placeholder={'Invoice Number'}
          value={filters.search_field ?? undefined}
          onChange={(value) => handleFilterChange('search_field', value)}
        />
        <Label className="text-xs text-gray-500">User Added Date:</Label>
        <DatePicker
          className="w-[150px] h-9 rounded-[3px] text-sm"
          value={filters.user_added_start_date || undefined}
          onChange={(date) =>
            handleFilterChange(
              'user_added_start_date',
              date ? date.toISOString().split('T')[0] : null
            )
          }
        />
        <DatePicker
          className="w-[150px] h-9 rounded-[3px] text-sm"
          value={filters.user_added_end_date || undefined}
          onChange={(date) =>
            handleFilterChange(
              'user_added_end_date',
              date ? date.toISOString().split('T')[0] : null
            )
          }
        />
        <Button size="sm" variant="lime" onClick={handleSearch}>
          Go
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">Loading payments...</div>
      ) : (
        <RadixTable columns={columns} data={data} />
      )}
    </div>
  );
};

export const PaymentsAccess: FC<PaymentsAccessProps> = ({
  user,
  targetInvoiceId,
}) => {
  const [subTab, setSubTab] = useState(0);

  useEffect(() => {
    if (targetInvoiceId) {
      setSubTab(0);
    }
  }, [targetInvoiceId]);

  const subTabs = useMemo(
    () => [
      {
        label: 'Invoices/Access',
        component: user?.id ? (
          <UserInvoices targetInvoiceId={targetInvoiceId} userId={user.id} />
        ) : null,
      },
      {
        label: 'Payments',
        component: user?.id ? <PaymentsTable userId={user.id} /> : null,
      },
    ],
    [user, targetInvoiceId]
  );

  if (!user) {
    return <div className="text-center py-8">No user selected</div>;
  }

  return (
    <div>
      <Tab tabActive={subTab} tabs={subTabs} onChangeTab={setSubTab} />
    </div>
  );
};
